// Sentinel's texts: the static demo's dictionaries (src/i18n/demo, en/de/it/fr)
// plus the strings added for the real app (./app.ts), on the shell's language
// signal, so the language the person picks in the frame is saved as their
// locale (/me) by the shell.
import { lang } from "@gimasi/giothub-shell";
import en from "./demo/en.js";
import de from "./demo/de.js";
import it from "./demo/it.js";
import fr from "./demo/fr.js";
import FP from "./demo/floorplans.js";
import M2 from "./demo/m2.js";
import M4 from "./demo/m4.js";
import M5 from "./demo/m5.js";
import APP from "./app";

type Dict = Record<string, string | ((v: Record<string, unknown>) => string)>;
const merge = (l: "en" | "de" | "it" | "fr", base: Dict): Dict => ({ ...base, ...FP[l], ...M2[l], ...M4[l], ...M5[l], ...APP[l] });
const DICTS: Record<string, Dict> = { en: merge("en", en), de: merge("de", de), it: merge("it", it), fr: merge("fr", fr) };
export const LOCALE: Record<string, string> = { en: "en-GB", de: "de-CH", it: "it-CH", fr: "fr-CH" };

/** t('key', {n: 3}) - falls back to English, then to the key itself. */
export function translate(l: string, key: string, vars?: Record<string, unknown>): string {
  const s = DICTS[l]?.[key] ?? DICTS.en[key] ?? key;
  if (typeof s === "function") return s(vars || {});
  return vars ? s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? "")) : s;
}

export interface T {
  (key: string, vars?: Record<string, unknown>): string;
  lang: string;
  locale: string;
  num: (n: number, d?: number) => string;
  temp: (v: number | null | undefined, d?: number) => string;
  time: (d: Date) => string;
  date: (d: Date) => string;
  ago: (iso: string | null | undefined) => string;
  dur: (mins: number) => string;
}

export function useT(): T {
  const l = (lang.value as string) || "en";
  const loc = LOCALE[l] || "en-GB";
  const t = ((k: string, v?: Record<string, unknown>) => translate(l, k, v)) as T;
  t.lang = l;
  t.locale = loc;
  t.num = (n, d = 0) => Number(n).toLocaleString(loc, { minimumFractionDigits: d, maximumFractionDigits: d });
  t.temp = (v, d = 1) => (v == null ? "—" : `${v >= 0 ? "+" : "−"}${Math.abs(v).toLocaleString(loc, { minimumFractionDigits: d, maximumFractionDigits: d })} °C`);
  t.time = (d) => d.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" });
  t.date = (d) => d.toLocaleDateString(loc, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  t.dur = (mins) => (mins < 60 ? translate(l, "u.min", { n: mins }) : translate(l, "u.hmin", { h: Math.floor(mins / 60), m: String(mins % 60).padStart(2, "0") }));
  t.ago = (iso) => {
    if (!iso) return translate(l, "x.never");
    const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
    if (m < 1) return translate(l, "x.justNow");
    if (m < 60) return translate(l, "x.minAgo", { n: m });
    if (m < 48 * 60) return translate(l, "x.hAgo", { n: Math.round(m / 60) });
    return new Date(iso).toLocaleDateString(loc, { day: "numeric", month: "short" });
  };
  return t;
}
